import * as React from 'react';
import { cn } from '@/util/cn';

// The ONLY place font-heading (Poppins) gets applied. Every level ships
// with a responsive default size + text-secondary color so callers can
// just write `<Heading level={2}>Title</Heading>` and get the right v3
// look. The `className` prop is for layout (mb-3, mb-4) or rare overrides
// (text-white on a hero, text-primary-summer for summer-mode sections);
// size and color don't need to be set in normal use.
//
// Pass `size` only for one-off non-responsive sizing (e.g. a 4xl quote in
// a dialog). Pass nothing for the level's default.

const BASE = 'font-heading font-semibold tracking-tight text-secondary';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const DEFAULT_BY_LEVEL: Record<HeadingLevel, string> = {
  1: 'text-3xl md:text-4xl lg:text-5xl',
  2: 'text-xl md:text-2xl',
  3: 'text-lg md:text-xl',
  4: 'text-base md:text-lg',
  5: 'text-sm md:text-base',
  6: 'text-xs md:text-sm',
};

const SIZE_MAP = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
} as const;

type HeadingSize = keyof typeof SIZE_MAP;

export interface HeadingProps
  extends Omit<React.HTMLAttributes<HTMLHeadingElement>, 'children'> {
  level?: HeadingLevel;
  size?: HeadingSize;
  children?: React.ReactNode;
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({
    level = 1, size, className, ...props
  }, ref) => {
    const Tag = `h${level}` as const;
    const sizeClass = size ? SIZE_MAP[size] : DEFAULT_BY_LEVEL[level];
    return (
      <Tag
        ref={ref}
        className={cn(BASE, sizeClass, className)}
        {...props}
      />
    );
  },
);
Heading.displayName = 'Heading';

export { Heading };
